import { supabase } from './supabase';
import { v4 as uuidv4 } from 'uuid';

export type RequestType = 'general_inquiry' | 'quote_request';

export interface RFQRequest {
  id?: string;
  user_id: string;
  title: string;
  description: string;
  request_type: RequestType;
  category_id?: string;
  subcategory_id?: string;
  product_category_id?: string;
  quantity?: number; // Optional for general inquiries
  budget_min?: number;
  budget_max?: number;
  target_price?: string; // Target price for quote request
  delivery_location?: string;
  delivery_date?: string; // Expected delivery date
  delivery_date_required?: string;
  specifications?: Record<string, any>;
  attachments?: string[];
  status: 'draft' | 'published' | 'responded' | 'closed' | 'fulfilled' | 'converted_to_rfq';
  expiry_date?: string;
  created_at?: string;
  updated_at?: string;
  visibility?: string;
  priority?: string;
  response?: string; // Response for general inquiries
  parent_inquiry_id?: string; // ID of the original inquiry if this RFQ was generated from an inquiry
  supplier_id?: string; // Supplier who converted the inquiry to RFQ
}

export interface RFQResponse {
  id?: string;
  rfq_id: string;
  supplier_id: string;
  quoted_price: number;
  currency: string;
  delivery_date: string;
  delivery_fee?: number;
  warranty_period?: string;
  payment_terms?: string;
  validity_period?: string;
  notes?: string;
  specifications_compliance?: Record<string, any>;
  attachments?: string[];
  status: 'submitted' | 'accepted' | 'rejected' | 'negotiating';
  created_at?: string;
  updated_at?: string;
  score?: number;
  delivery_method?: string;
  accounts?: {
    id: string;
    name: string;
    logo?: string;
    rating?: number;
  };
  rfq_requests?: RFQRequest;
}

export interface RFQComparison {
  rfq: RFQRequest;
  responses: (RFQResponse & {
    supplier: {
      id: string;
      name: string;
      logo?: string;
      rating?: number;
    };
    complianceScore?: number;
    priceScore?: number;
    deliveryScore?: number;
    overallScore?: number;
  })[];
}

export class RFQService {
  async createRFQ(rfq: Omit<RFQRequest, 'id'>): Promise<RFQRequest | null> {
    try {
      // Start a transaction to insert RFQ and specifications
      const now = new Date().toISOString();
      const rfqId = uuidv4();

      // Extract specifications and attachments before insertion
      const { specifications, attachments, ...rfqData } = rfq;
      
      // Map the interface fields to the actual database columns
      const dbRfqData = {
        id: rfqId,
        user_id: rfqData.user_id,
        title: rfqData.title,
        description: rfqData.description,
        status: rfqData.status || 'draft',
        quantity: rfqData.quantity || 1,
        category_id: rfqData.category_id || null,
        subcategory_id: rfqData.subcategory_id || null,
        product_category_id: rfqData.product_category_id || null,
        budget_min: rfqData.budget_min || null,
        budget_max: rfqData.budget_max || null,
        currency: rfqData.target_price ? 'USD' : null, // Use USD as default currency
        delivery_location: rfqData.delivery_location || null,
        delivery_date_required: rfqData.delivery_date ? new Date(rfqData.delivery_date).toISOString() : null,
        expiry_date: rfqData.expiry_date ? new Date(rfqData.expiry_date).toISOString() : null,
        visibility: rfqData.visibility || 'public',
        priority: rfqData.priority || 'normal',
        created_at: now,
        updated_at: now
      };
      
      // Insert main RFQ record
      const { data, error } = await supabase
        .from('rfq_requests')
        .insert([dbRfqData])
        .select();
      
      if (error) {
        throw error;
      }

      // If we have specifications, insert them into the rfq_specifications table
      if (specifications && Object.keys(specifications).length > 0) {
        const specInserts = Object.entries(specifications).map(([key, value]) => ({
          id: uuidv4(),
          rfq_id: rfqId,
          specification_key: key,
          specification_value: typeof value === 'object' ? JSON.stringify(value) : String(value),
          required: true,
          created_at: now,
          updated_at: now
        }));

        const { error: specsError } = await supabase
          .from('rfq_specifications')
          .insert(specInserts);

        if (specsError) {
          console.error('Error inserting specifications:', specsError);
          // Continue even if specs insertion fails
        }
      }

      // If we have attachments, insert them into the rfq_attachments table
      if (attachments && attachments.length > 0) {
        const attachmentInserts = attachments.map(filePath => ({
          id: uuidv4(),
          rfq_id: rfqId,
          response_id: null, // This is for RFQ attachments, not response attachments
          file_name: filePath.split('/').pop() || 'attachment', // Extract filename from path
          file_path: filePath,
          file_type: this.getFileTypeFromPath(filePath),
          file_size: null, // We don't have file size information
          description: null, // No description provided
          uploaded_by: rfqData.user_id, // The user creating the RFQ
          created_at: now
        }));

        const { error: attachmentsError } = await supabase
          .from('rfq_attachments')
          .insert(attachmentInserts);

        if (attachmentsError) {
          console.error('Error inserting attachments:', attachmentsError);
          // Continue even if attachments insertion fails
        }
      }

      // Return the created RFQ with the original interface structure
      const createdRFQ = data?.[0];
      if (createdRFQ) {
        return {
          ...createdRFQ,
          request_type: 'quote_request', // Default request type
          target_price: rfqData.target_price,
          delivery_date: rfqData.delivery_date,
          specifications: specifications || {},
          attachments: attachments || [],
          supplier_id: rfqData.supplier_id
        } as RFQRequest;
      }

      return null;
    } catch (error) {
      console.error('Error creating request:', error);
      return null;
    }
  }

  private getFileTypeFromPath(path: string): string {
    const extension = path.split('.').pop()?.toLowerCase() || '';
    const mimeTypes: Record<string, string> = {
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'txt': 'text/plain'
    };
    
    return mimeTypes[extension] || 'application/octet-stream';
  }
  
  async getRFQById(id: string): Promise<RFQRequest | null> {
    try {
      // Get the main RFQ data
      const { data: rfqData, error } = await supabase
        .from('rfq_requests')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        throw error;
      }
      
      if (!rfqData) {
        return null;
      }
      
      // Get specifications
      const { data: specsData, error: specsError } = await supabase
        .from('rfq_specifications')
        .select('specification_key, specification_value')
        .eq('rfq_id', id);
      
      if (specsError) {
        console.error('Error fetching specifications:', specsError);
        // Continue even if specs fetch fails
      }
      
      // Get attachments
      const { data: attachmentsData, error: attachmentsError } = await supabase
        .from('rfq_attachments')
        .select('file_path')
        .eq('rfq_id', id);
      
      if (attachmentsError) {
        console.error('Error fetching attachments:', attachmentsError);
        // Continue even if attachments fetch fails
      }
      
      // Convert specifications to object
      const specifications: Record<string, any> = {};
      if (specsData && specsData.length > 0) {
        specsData.forEach(spec => {
          // Try to parse JSON values
          try {
            specifications[spec.specification_key] = JSON.parse(spec.specification_value);
          } catch {
            specifications[spec.specification_key] = spec.specification_value;
          }
        });
      }
      
      // Extract attachment paths
      const attachments = attachmentsData ? attachmentsData.map(a => a.file_path) : [];
      
      // Combine all data
      return {
        ...rfqData,
        specifications,
        attachments
      };
    } catch (error) {
      console.error('Error fetching RFQ:', error);
      return null;
    }
  }
  
  async getUserRFQs(userId: string): Promise<RFQRequest[]> {
    try {
      const { data, error } = await supabase
        .from('rfq_requests')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching user RFQs:', error);
      return [];
    }
  }
  
  async getOpenRFQsForSupplier(supplierId: string): Promise<RFQRequest[]> {
    try {
      // For now, return all published and non-expired RFQs
      // TODO: Implement proper supplier category filtering when supplier_categories table is available
      const { data, error } = await supabase
        .from('rfq_requests')
        .select('*')
        .eq('status', 'published')
        .gte('expiry_date', new Date().toISOString())
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching open RFQs for supplier:', error);
      return [];
    }
  }
  
  async submitRFQResponse(response: Omit<RFQResponse, 'id'>): Promise<RFQResponse | null> {
    try {
      const now = new Date().toISOString();
      const responseId = uuidv4();

      // Extract specifications compliance before insertion
      const { specifications_compliance, attachments, ...responseData } = response;
      
      // Insert main response record
      const { data, error } = await supabase
        .from('rfq_responses')
        .insert([{
          ...responseData,
          id: responseId,
          created_at: now,
          updated_at: now
        }])
        .select();
      
      if (error) {
        throw error;
      }

      // If we have specifications compliance, insert them
      if (specifications_compliance && Object.keys(specifications_compliance).length > 0) {
        // First, fetch the specification IDs from the RFQ
        const { data: specsData, error: specsError } = await supabase
          .from('rfq_specifications')
          .select('id, specification_key')
          .eq('rfq_id', response.rfq_id);

        if (specsError) {
          console.error('Error fetching specifications for compliance:', specsError);
          // Continue even if specs fetch fails
        } else if (specsData && specsData.length > 0) {
          // Create a map of specification keys to their IDs
          const specKeyToIdMap: Record<string, string> = {};
          specsData.forEach(spec => {
            specKeyToIdMap[spec.specification_key] = spec.id;
          });

          // Create compliance records for each specification
          const complianceInserts = Object.entries(specifications_compliance).map(([specKey, complianceData]) => {
            const specId = specKeyToIdMap[specKey];
            if (!specId) return null; // Skip if we couldn't find the spec ID

            // If complianceData is a boolean, use it directly
            // If it's an object, extract complies, alternative and notes
            let complies = true;
            let alternative = '';
            let notes = '';

            if (typeof complianceData === 'boolean') {
              complies = complianceData;
            } else if (typeof complianceData === 'object') {
              complies = complianceData.complies !== false;
              alternative = complianceData.alternative || '';
              notes = complianceData.notes || '';
            }

            return {
              id: uuidv4(),
              response_id: responseId,
              specification_id: specId,
              complies,
              alternative_specification: alternative,
              notes,
              created_at: now,
              updated_at: now
            };
          }).filter(item => item !== null) as any[];

          if (complianceInserts.length > 0) {
            const { error: complianceError } = await supabase
              .from('rfq_specifications_compliance')
              .insert(complianceInserts);

            if (complianceError) {
              console.error('Error inserting specification compliance:', complianceError);
              // Continue even if compliance insertion fails
            }
          }
        }
      }

      // Insert attachments if provided
      if (attachments && attachments.length > 0) {
        const attachmentInserts = attachments.map(attachment => ({
          id: uuidv4(),
          response_id: responseId,
          file_path: attachment,
          file_name: attachment.split('/').pop() || 'file',
          file_type: this.getFileTypeFromPath(attachment),
          uploaded_by: responseData.supplier_id,
          created_at: now
        }));

        const { error: attachmentError } = await supabase
          .from('rfq_attachments')
          .insert(attachmentInserts);

        if (attachmentError) {
          console.error('Error inserting attachments:', attachmentError);
          // Continue even if attachments insertion fails
        }
      }
      
      // Return the created response with its ID
      const createdResponse = data && data.length > 0 ? {
        ...data[0],
        specifications_compliance,
        attachments
      } : null;
      
      return createdResponse;
    } catch (error) {
      console.error('Error submitting RFQ response:', error);
      return null;
    }
  }
  
  /**
   * Update the status of an RFQ request
   * @param rfqId The RFQ ID
   * @param status The new status
   * @returns Promise resolving to void
   */
  async updateRFQStatus(rfqId: string, status: RFQRequest['status'] = 'responded'): Promise<void> {
    try {
      // Update RFQ status - public method that can be called outside the class
      const { error } = await supabase
        .from('rfq_requests')
        .update({ 
          status,
          updated_at: new Date().toISOString() 
        })
        .eq('id', rfqId);
        
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error updating RFQ status:', error);
    }
  }
  
  /**
   * Update an existing RFQ request
   * @param id The RFQ ID to update
   * @param updates The data to update
   * @returns The updated RFQ or null if update failed
   */
  async updateRFQ(id: string, updates: Partial<RFQRequest>): Promise<RFQRequest | null> {
    try {
      const { specifications, attachments, ...updateData } = updates;
      
      // Update the main RFQ record
      const { data, error } = await supabase
        .from('rfq_requests')
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select();
      
      if (error) {
        throw error;
      }
      
      if (!data || data.length === 0) {
        return null;
      }
      
      // Get the updated RFQ with all its data
      return this.getRFQById(id);
    } catch (error) {
      console.error('Error updating RFQ:', error);
      return null;
    }
  }
  
  async getRFQResponses(rfqId: string): Promise<RFQResponse[]> {
    try {
      // Get the main response data
      const { data: responseData, error } = await supabase
        .from('rfq_responses')
        .select(`
          *,
          accounts:supplier_id (
            id,
            name,
            logo,
            rating
          )
        `)
        .eq('rfq_id', rfqId)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      if (!responseData || responseData.length === 0) {
        return [];
      }

      // Get all the response IDs
      const responseIds = responseData.map(response => response.id);

      // Fetch specifications compliance for all responses
      const { data: complianceData, error: complianceError } = await supabase
        .from('rfq_specifications_compliance')
        .select(`*, rfq_specifications:specification_id (specification_key, specification_value)`)
        .in('response_id', responseIds);

      if (complianceError) {
        console.error('Error fetching compliance data:', complianceError);
        // Continue even if compliance fetch fails
      }

      // Fetch attachments for all responses
      const { data: attachmentsData, error: attachmentsError } = await supabase
        .from('rfq_attachments')
        .select('response_id, file_path')
        .in('response_id', responseIds);

      if (attachmentsError) {
        console.error('Error fetching attachment data:', attachmentsError);
        // Continue even if attachments fetch fails
      }

      // Create maps for compliance and attachments by response ID
      const complianceByResponse: Record<string, Record<string, any>> = {};
      const attachmentsByResponse: Record<string, string[]> = {};

      // Organize compliance data by response ID
      if (complianceData && complianceData.length > 0) {
        complianceData.forEach(compliance => {
          const responseId = compliance.response_id;
          const specKey = compliance.rfq_specifications?.specification_key;
          
          if (specKey) {
            if (!complianceByResponse[responseId]) {
              complianceByResponse[responseId] = {};
            }
            
            complianceByResponse[responseId][specKey] = {
              complies: compliance.complies,
              alternative: compliance.alternative_specification,
              notes: compliance.notes
            };
          }
        });
      }

      // Organize attachment data by response ID
      if (attachmentsData && attachmentsData.length > 0) {
        attachmentsData.forEach(attachment => {
          const responseId = attachment.response_id;
          
          if (!attachmentsByResponse[responseId]) {
            attachmentsByResponse[responseId] = [];
          }
          
          attachmentsByResponse[responseId].push(attachment.file_path);
        });
      }

      // Combine all the data into complete response objects
      const completeResponses = responseData.map(response => {
        return {
          ...response,
          specifications_compliance: complianceByResponse[response.id] || {},
          attachments: attachmentsByResponse[response.id] || []
        };
      });
      
      return completeResponses;
    } catch (error) {
      console.error('Error fetching RFQ responses:', error);
      return [];
    }
  }
  
  async getSupplierResponses(supplierId: string): Promise<RFQResponse[]> {
    try {
      // Get the main response data
      const { data: responseData, error } = await supabase
        .from('rfq_responses')
        .select(`*, rfq_requests:rfq_id (*)`)
        .eq('supplier_id', supplierId)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      if (!responseData || responseData.length === 0) {
        return [];
      }

      // Get all the response IDs
      const responseIds = responseData.map(response => response.id);

      // Fetch specifications compliance for all responses
      const { data: complianceData, error: complianceError } = await supabase
        .from('rfq_specifications_compliance')
        .select(`*, rfq_specifications:specification_id (specification_key, specification_value)`)
        .in('response_id', responseIds);

      if (complianceError) {
        console.error('Error fetching compliance data:', complianceError);
        // Continue even if compliance fetch fails
      }

      // Fetch attachments for all responses
      const { data: attachmentsData, error: attachmentsError } = await supabase
        .from('rfq_attachments')
        .select('response_id, file_path')
        .in('response_id', responseIds);

      if (attachmentsError) {
        console.error('Error fetching attachment data:', attachmentsError);
        // Continue even if attachments fetch fails
      }

      // Create maps for compliance and attachments by response ID
      const complianceByResponse: Record<string, Record<string, any>> = {};
      const attachmentsByResponse: Record<string, string[]> = {};

      // Organize compliance data by response ID
      if (complianceData && complianceData.length > 0) {
        complianceData.forEach(compliance => {
          const responseId = compliance.response_id;
          const specKey = compliance.rfq_specifications?.specification_key;
          
          if (specKey) {
            if (!complianceByResponse[responseId]) {
              complianceByResponse[responseId] = {};
            }
            
            complianceByResponse[responseId][specKey] = {
              complies: compliance.complies,
              alternative: compliance.alternative_specification,
              notes: compliance.notes
            };
          }
        });
      }

      // Organize attachment data by response ID
      if (attachmentsData && attachmentsData.length > 0) {
        attachmentsData.forEach(attachment => {
          const responseId = attachment.response_id;
          
          if (!attachmentsByResponse[responseId]) {
            attachmentsByResponse[responseId] = [];
          }
          
          attachmentsByResponse[responseId].push(attachment.file_path);
        });
      }

      // Combine all the data into complete response objects
      const completeResponses = responseData.map(response => {
        return {
          ...response,
          specifications_compliance: complianceByResponse[response.id] || {},
          attachments: attachmentsByResponse[response.id] || []
        };
      });
      
      return completeResponses;
    } catch (error) {
      console.error('Error fetching supplier responses:', error);
      return [];
    }
  }
  
  async getRFQComparison(rfqId: string): Promise<RFQComparison | null> {
    try {
      // Get the RFQ details
      const rfq = await this.getRFQById(rfqId);
      if (!rfq) {
        throw new Error('RFQ not found');
      }
      
      // Get all responses for this RFQ with supplier details
      const responses = await this.getRFQResponses(rfqId);
      if (!responses || responses.length === 0) {
        return { rfq, responses: [] };
      }
      
      // Calculate scores for each response
      const scoredResponses = responses.map(response => {
        const supplierDetails = response.accounts;
        
        // Calculate compliance score based on matching specifications
        const complianceScore = this.calculateComplianceScore(
          rfq.specifications || {},
          response.specifications_compliance || {}
        );
        
        // Calculate price score (lower is better)
        let priceScore = 75; // Default score
        
        if (rfq.budget_min && rfq.budget_max) {
          const midBudget = (rfq.budget_min + rfq.budget_max) / 2;
          const percentFromMid = (response.quoted_price - midBudget) / midBudget * 100;
          
          if (percentFromMid <= -20) {
            // More than 20% below mid budget
            priceScore = 100;
          } else if (percentFromMid <= -10) {
            // 10-20% below mid budget
            priceScore = 90;
          } else if (percentFromMid <= 0) {
            // 0-10% below mid budget
            priceScore = 85;
          } else if (percentFromMid <= 10) {
            // 0-10% above mid budget
            priceScore = 75;
          } else if (percentFromMid <= 20) {
            // 10-20% above mid budget
            priceScore = 65;
          } else if (percentFromMid <= 30) {
            // 20-30% above mid budget
            priceScore = 50;
          } else {
            // More than 30% above mid budget
            priceScore = 40;
          }
        }
        
        // Calculate delivery score based on matching requested date
        const deliveryDate = new Date(response.delivery_date);
        const requestedDate = rfq.delivery_date_required 
          ? new Date(rfq.delivery_date_required)
          : null;
        
        let deliveryScore = 75; // Default score
        
        if (requestedDate) {
          const daysDifference = Math.ceil(
            (deliveryDate.getTime() - requestedDate.getTime()) / (1000 * 60 * 60 * 24)
          );
          
          if (daysDifference <= 0) {
            // On or before requested date
            deliveryScore = 100;
          } else if (daysDifference <= 7) {
            // Within one week after requested date
            deliveryScore = 90 - (daysDifference * 2.5);
          } else if (daysDifference <= 14) {
            // Within two weeks after requested date
            deliveryScore = 75 - ((daysDifference - 7) * 2.5);
          } else {
            // More than two weeks after requested date
            deliveryScore = Math.max(50 - ((daysDifference - 14) * 1.5), 10);
          }
        }
        
        // Calculate overall score (weighted average)
        const weights = {
          compliance: 0.4,
          price: 0.35,
          delivery: 0.25
        };
        
        const overallScore = Math.round(
          (complianceScore * weights.compliance) + 
          (priceScore * weights.price) + 
          (deliveryScore * weights.delivery)
        );
        
        return {
          ...response,
          supplier: {
            id: supplierDetails?.id || response.supplier_id,
            name: supplierDetails?.name || 'Unknown Supplier',
            logo: supplierDetails?.logo,
            rating: supplierDetails?.rating
          },
          complianceScore,
          priceScore,
          deliveryScore,
          overallScore
        };
      });
      
      // Sort by overall score (highest first)
      scoredResponses.sort((a, b) => (b.overallScore || 0) - (a.overallScore || 0));
      
      return { rfq, responses: scoredResponses };
    } catch (error) {
      console.error('Error generating RFQ comparison:', error);
      return null;
    }
  }
  
  private calculateComplianceScore(
    requestedSpecs: Record<string, any>,
    suppliedSpecs: Record<string, any>
  ): number {
    // Extract all keys from both objects
    const allKeys = new Set([
      ...Object.keys(requestedSpecs),
      ...Object.keys(suppliedSpecs)
    ]);
    
    if (allKeys.size === 0) {
      return 100; // No specifications to compare
    }
    
    let matchCount = 0;
    let totalCount = 0;
    
    allKeys.forEach(key => {
      if (key in requestedSpecs) {
        totalCount++;
        
        // Check if key exists in supplied specs
        if (key in suppliedSpecs) {
          const requestedValue = requestedSpecs[key];
          const suppliedValue = suppliedSpecs[key];
          
          // Simple equality check for now
          // Could be enhanced for more sophisticated comparison
          if (requestedValue === suppliedValue) {
            matchCount++;
          } else if (typeof requestedValue === 'number' && typeof suppliedValue === 'number') {
            // For numeric values, give partial credit based on how close the values are
            const percentDifference = Math.abs((requestedValue - suppliedValue) / requestedValue);
            if (percentDifference <= 0.1) { // Within 10%
              matchCount += 0.9;
            } else if (percentDifference <= 0.25) { // Within 25%
              matchCount += 0.75;
            } else if (percentDifference <= 0.5) { // Within 50%
              matchCount += 0.5;
            }
          }
        }
      }
    });
    
    return Math.round((matchCount / totalCount) * 100);
  }
  
    /**
   * Converts a general inquiry to a formal RFQ
   * @param inquiryId ID of the inquiry to convert
   * @param supplierId ID of the supplier creating the RFQ
   * @param rfqData Additional RFQ data to include
   * @returns Created RFQ or null if conversion fails
   */
  async convertInquiryToRFQ(
    inquiryId: string,
    supplierId: string,
    rfqData: Partial<RFQRequest>
  ): Promise<RFQRequest | null> {
    try {
      // First, fetch the original inquiry
      const { data: inquiryData, error: inquiryError } = await supabase
        .from('rfq_requests')
        .select('*')
        .eq('id', inquiryId)
        .eq('request_type', 'general_inquiry')
        .single();
      
      if (inquiryError || !inquiryData) {
        console.error('Error fetching inquiry:', inquiryError);
        return null;
      }
      
      // Create a new RFQ based on the inquiry
      // Ensure all required fields have values
      const newRfqData = {
        user_id: inquiryData.user_id, // Keep the original buyer
        title: rfqData.title || `RFQ for: ${inquiryData.title}`,
        description: rfqData.description || inquiryData.description,
        request_type: 'quote_request' as RequestType,
        category_id: rfqData.category_id || inquiryData.category_id,
        subcategory_id: rfqData.subcategory_id || inquiryData.subcategory_id,
        product_category_id: rfqData.product_category_id || inquiryData.product_category_id,
        quantity: rfqData.quantity || 1,
        specifications: rfqData.specifications || inquiryData.specifications,
        attachments: rfqData.attachments || inquiryData.attachments,
        status: 'published' as RFQRequest['status'],
        parent_inquiry_id: inquiryId,
        supplier_id: supplierId,
        target_price: rfqData.target_price,
        delivery_date: rfqData.delivery_date,
        budget_min: rfqData.budget_min,
        budget_max: rfqData.budget_max,
        delivery_location: rfqData.delivery_location || inquiryData.delivery_location
      };
      
      // Create the new RFQ
      const newRfq = await this.createRFQ(newRfqData);
      
      if (newRfq) {
        // Update the status of the original inquiry
        await supabase
          .from('rfq_requests')
          .update({ 
            status: 'converted_to_rfq',
            updated_at: new Date().toISOString() 
          })
          .eq('id', inquiryId);
      }
      
      return newRfq;
    } catch (error) {
      console.error('Error converting inquiry to RFQ:', error);
      return null;
    }
  }
  
  async updateRFQResponseStatus(
    responseId: string, 
    status: RFQResponse['status']
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('rfq_responses')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', responseId);
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Error updating RFQ response status:', error);
      return false;
    }
  }
}
